import { useUser } from "@clerk/clerk-react";
import { useRouterState } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ActiveTransportersSection, type DashboardDriverCard } from "../components/dashboard/ActiveTransportersSection";
import { DashboardMetricCards } from "../components/dashboard/DashboardMetricCards";
import { DeliveryMapPanel, type MapDriverPin } from "../components/dashboard/DeliveryMapPanel";
import { useTransporterDriversLive } from "../hooks/useTransporterDriversLive";
import { useTransporterRunEta } from "../hooks/useTransporterRunEta";
import { RecentDeliveryLogsTable } from "../components/dashboard/RecentDeliveryLogsTable";
import {
  fetchTransporterActiveRuns,
  fetchTransporterEarningsSummary,
  fetchTransporterFleetStatus,
  fetchTransporterRunHistory,
  type TransporterFleetDriverDto,
} from "../lib/fetch-transporter-delivery-ops";
import { useTransporterWorkspace } from "../workspace/transporter-workspace-context";

function buildDriverRows(
  drivers: TransporterFleetDriverDto[],
  vehicles: { licensePlate: string }[],
  inFlightOrderCountByDriverId: Map<string, number>,
): DashboardDriverCard[] {
  const defaultVehicle = vehicles[0]?.licensePlate ?? "Fleet vehicle";
  return drivers.map((d) => {
    // ADR-115: a "run" is a shift, not an order. A driver who's on shift
    // but whose only order just terminated should read "Available", not
    // "In Transit" — pre-fix `activeRunDriverIds.has(driverId)` flipped to
    // `in_transit` for the whole shift's lifetime regardless of whether any
    // order was actually in flight. Count distinct in-flight orders
    // (`sales_orders` with at least one non-terminal stop) instead.
    const orderCount = inFlightOrderCountByDriverId.get(d.driverId) ?? 0;
    let status: DashboardDriverCard["status"] = "offline";
    if (orderCount > 0) status = "in_transit";
    else if (d.onShift) status = "available";
    return {
      driverId: d.driverId,
      name: d.fullName,
      vehicleLabel: defaultVehicle,
      status,
      orderCount,
      phoneDisplay: "—",
    };
  });
}

export function DashboardScreen() {
  const headerSearch = useRouterState({
    select: (s) =>
      s.location.pathname.startsWith("/workspace/dashboard")
        ? new URLSearchParams(s.location.search).get("q")?.trim() ?? ""
        : "",
  });
  const { transporterId, getToken } = useTransporterWorkspace();
  const { user } = useUser();
  const firstName = user?.firstName?.trim() || user?.username?.trim() || "there";

  const [loadError, setLoadError] = useState<string | null>(null);
  const [drivers, setDrivers] = useState<DashboardDriverCard[]>([]);
  const [logs, setLogs] = useState<Awaited<ReturnType<typeof fetchTransporterRunHistory>>["runs"]>([]);
  const [metrics, setMetrics] = useState({
    totalDeliveries: 0,
    inProgressPercent: 0,
    avgMinutes: null as number | null,
    completedOrders: 0,
    completedChangePercent: null as number | null,
  });
  const [selectedDriverId, setSelectedDriverId] = useState<string | null>(null);
  /**
   * driverId → active runId from the most recent `fetchTransporterActiveRuns` poll. Used to look
   * up the right `run.eta` entry for the selected driver. Refreshed by `load()` every dashboard
   * fetch; `useTransporterRunEta` updates the ETA payload itself in real-time.
   */
  const [activeRunIdByDriverId, setActiveRunIdByDriverId] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    if (!transporterId) return;
    setLoadError(null);
    try {
      const [fleet, active, history, earnings] = await Promise.all([
        fetchTransporterFleetStatus(getToken, transporterId),
        fetchTransporterActiveRuns(getToken, transporterId),
        fetchTransporterRunHistory(getToken, transporterId, { limit: 10 }),
        fetchTransporterEarningsSummary(getToken, transporterId, { dailyLookbackDays: 30 }),
      ]);
      // Sum per-run in-flight order counts across the shift's runs (today there
      // is at most one open run per driver, but the sum is correct regardless).
      const inFlightByDriver = new Map<string, number>();
      const runIdByDriver: Record<string, string> = {};
      for (const r of active.runs) {
        runIdByDriver[r.driverId] = r.id;
        const add = r.inFlightOrderCount ?? 0;
        inFlightByDriver.set(r.driverId, (inFlightByDriver.get(r.driverId) ?? 0) + add);
      }
      setActiveRunIdByDriverId(runIdByDriver);
      setDrivers(buildDriverRows(fleet.drivers, fleet.vehicles, inFlightByDriver));
      setLogs(history.runs);

      const onShift = fleet.drivers.filter((d) => d.onShift).length;
      // "In progress" = drivers actively delivering at least one order, not
      // "drivers with any open shift" (pre-fix: `active.runs.length`).
      const inProg = [...inFlightByDriver.values()].filter((c) => c > 0).length;
      const pct = onShift > 0 ? Math.min(100, Math.round((inProg / onShift) * 100)) : 0;

      let change: number | null = null;
      const daily = earnings.daily;
      if (daily.length >= 2) {
        const a = daily[daily.length - 1]?.completedDeliveryCount ?? 0;
        const b = daily[daily.length - 2]?.completedDeliveryCount ?? 0;
        if (b > 0) change = Math.round(((a - b) / b) * 1000) / 10;
      }

      setMetrics({
        totalDeliveries: earnings.allTime.completedDeliveryCount,
        inProgressPercent: pct,
        avgMinutes: null,
        completedOrders: earnings.monthToDate.completedDeliveryCount,
        completedChangePercent: change,
      });
    } catch (e) {
      setLoadError(e instanceof Error ? e.message : "load_failed");
      setDrivers([]);
      setLogs([]);
      setActiveRunIdByDriverId({});
    }
  }, [transporterId, getToken]);

  useEffect(() => {
    void load();
    // Mirror dispensary `DarkOrdersCard`'s 30s React Query `refetchInterval` so
    // the dashboard "in progress %" metric and per-driver active-run pill clear
    // within 30s of a terminal-stop transition (e.g. driver fails delivery)
    // instead of requiring a full reload.
    const intervalId = setInterval(() => void load(), 30_000);
    return () => {
      clearInterval(intervalId);
    };
  }, [load]);

  const mapPins: MapDriverPin[] = useMemo(() => {
    const q = headerSearch.toLowerCase();
    const list = q
      ? drivers.filter((d) => {
          const hay = [d.name, d.vehicleLabel, d.driverId, d.phoneDisplay, d.status].join(" ").toLowerCase();
          return hay.includes(q);
        })
      : drivers;
    return list.map((d) => ({
      driverId: d.driverId,
      name: d.name,
      hasActiveRun: d.status === "in_transit",
    }));
  }, [drivers, headerSearch]);

  const activeCount = useMemo(() => drivers.filter((d) => d.status !== "offline").length, [drivers]);

  const livePositions = useTransporterDriversLive(transporterId);
  const runEtaByRunId = useTransporterRunEta(transporterId);
  const selectedRunEta = useMemo(() => {
    if (!selectedDriverId) return undefined;
    const runId = activeRunIdByDriverId[selectedDriverId];
    return runId ? runEtaByRunId[runId] : undefined;
  }, [selectedDriverId, activeRunIdByDriverId, runEtaByRunId]);

  if (!transporterId) {
    return (
      <div className="td-surface p-6 text-sm text-td-muted">
        Select a transporter scope to load the dashboard.
      </div>
    );
  }

  return (
    <div className="w-full">
      {loadError ? (
        <p className="mb-4 rounded-2xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 shadow-td-card">
          {loadError}{" "}
          <button type="button" className="underline" onClick={() => void load()}>
            Retry
          </button>
        </p>
      ) : null}

      <div className="mb-7 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-td-ink">Welcome back, {firstName}</h2>
          <div className="mt-1 flex items-center gap-1.5">
            <CheckIcon className="h-4 w-4 text-td-green" />
            <span className="text-sm font-medium text-[#555555]">{activeCount} active transporters</span>
          </div>
        </div>
        <button
          type="button"
          className="flex items-center gap-2 rounded-xl border border-[#DDDDDD] bg-white px-4 py-2.5 text-sm font-medium text-td-ink transition-colors hover:bg-[#F5F5F3]"
        >
          <PlusIcon className="h-4 w-4" />
          Add Transporter
        </button>
      </div>

      <DashboardMetricCards metrics={metrics} />

      <div className="flex flex-col gap-5 lg:flex-row lg:items-start">
        <div className="min-w-0 flex-1 space-y-8">
          <ActiveTransportersSection
            drivers={drivers}
            globalSearch={headerSearch}
            selectedDriverId={selectedDriverId}
            onSelectDriverId={setSelectedDriverId}
          />
          <RecentDeliveryLogsTable rows={logs} filterQuery={headerSearch} />
        </div>
        <DeliveryMapPanel
          drivers={mapPins}
          positions={livePositions}
          selectedRunEta={selectedRunEta}
          selectedDriverId={selectedDriverId}
          onSelectDriverId={setSelectedDriverId}
        />
      </div>
    </div>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}
